import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useStore } from "../../zustand/store";
import './userDetails.css';
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import {
    assetsUrlPath, hitApi, NoDataFoundDataTable,
    Pagination, skeletonLoader,
    getNameInitials,
} from "../../components/commonAction";
import { useParams, Link } from "react-router-dom";
import moment from "moment";

// ── small helpers ──────────────────────────────────────────────────────────────

function StatusPill({ value, map }) {
    const cfg = map[value] || { label: value || '—', cls: 'pxl-ud-pill--muted' };
    return <span className={`pxl-ud-pill ${cfg.cls}`}>{cfg.label}</span>;
}

const SUBSCRIPTION_STATUS_MAP = {
    completed: { label: 'Completed', cls: 'pxl-ud-pill--success' },
    pending:   { label: 'Pending',   cls: 'pxl-ud-pill--warning' },
    failed:    { label: 'Failed',    cls: 'pxl-ud-pill--danger'  },
    refunded:  { label: 'Refunded',  cls: 'pxl-ud-pill--muted'   },
};

const TRANSACTION_STATUS_MAP = {
    completed: { label: 'Completed', cls: 'pxl-ud-pill--success' },
    pending:   { label: 'Pending',   cls: 'pxl-ud-pill--warning' },
    failed:    { label: 'Failed',    cls: 'pxl-ud-pill--danger'  },
    refunded:  { label: 'Refunded',  cls: 'pxl-ud-pill--muted'   },
};

const TABS = ['Login History', 'Subscriptions', 'Transactions'];

// ── component ──────────────────────────────────────────────────────────────────

const UserDetails = ({ pageTitle }) => {
    const store = useStore(s => s);
    const { id } = useParams();

    const [user,    setUser]    = useState(null);
    const [activeTab, setActiveTab] = useState(0);

    // ── login history state ──────────────────────────────────────────────────
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;

    const [loginState, setLoginState] = useState({
        data: [], limit: 10, page: 1, totalRecords: '',
        loading: false, startDate: null, endDate: null,
    });

    // ── subscriptions state ──────────────────────────────────────────────────
    const [subState, setSubState] = useState({
        data: [], limit: 10, page: 1, totalRecords: '', loading: false,
    });

    // ── transactions state ───────────────────────────────────────────────────
    const [txState, setTxState] = useState({
        data: [], limit: 10, page: 1, totalRecords: '', loading: false,
    });

    // ── initial fetch ────────────────────────────────────────────────────────
    useEffect(() => {
        fetchUserDetails();
        fetchLoginHistory();
        fetchSubscriptions();
        fetchTransactions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { fetchLoginHistory(); }, [loginState.page, loginState.endDate]);
    useEffect(() => { fetchSubscriptions(); }, [subState.page]);
    useEffect(() => { fetchTransactions();  }, [txState.page]);

    useEffect(() => {
        if (dateRange[0] && dateRange[1]) {
            setLoginState(p => ({
                ...p,
                startDate: moment(dateRange[0]).format('YYYY-MM-DD'),
                endDate:   moment(dateRange[1]).format('YYYY-MM-DD'),
            }));
        }
    }, [dateRange]);

    // ── fetch helpers ────────────────────────────────────────────────────────
    const fetchUserDetails = () => {
        hitApi({ url: 'user/user-details', method: 'POST', data: { id } }, (resp) => {
            if (resp.status && resp.user) {
                setUser(resp.user);
            }
        });
    };

    const fetchLoginHistory = () => {
        setLoginState(p => ({ ...p, loading: true }));
        const { limit, page, startDate: sd, endDate: ed } = loginState;
        hitApi({
            url: 'user/user-login-history', method: 'GET',
            data: { limit, page, user_id: id, startDate: sd, endDate: ed },
        }, (resp) => {
            if (resp.status) {
                setLoginState(p => ({
                    ...p, loading: false,
                    data: resp.data.records,
                    totalRecords: resp.data.totalRecords,
                }));
            } else {
                setLoginState(p => ({ ...p, loading: false }));
            }
        });
    };

    const fetchSubscriptions = () => {
        setSubState(p => ({ ...p, loading: true }));
        const params = new URLSearchParams({ userId: id, type: 'plan', page: subState.page, limit: subState.limit });
        hitApi({ url: `admin/billing/transactions?${params}`, method: 'GET', data: {}, alert: false }, (resp) => {
            if (resp?.status) {
                const d = resp.data;
                setSubState(p => ({
                    ...p, loading: false,
                    data: d?.transactions || d?.records || [],
                    totalRecords: d?.pagination?.total || d?.totalRecords || 0,
                }));
            } else {
                setSubState(p => ({ ...p, loading: false, data: [] }));
            }
        });
    };

    const fetchTransactions = () => {
        setTxState(p => ({ ...p, loading: true }));
        const params = new URLSearchParams({ userId: id, page: txState.page, limit: txState.limit });
        hitApi({ url: `admin/billing/transactions?${params}`, method: 'GET', data: {}, alert: false }, (resp) => {
            if (resp?.status) {
                const d = resp.data;
                setTxState(p => ({
                    ...p, loading: false,
                    data: d?.transactions || d?.records || [],
                    totalRecords: d?.pagination?.total || d?.totalRecords || 0,
                }));
            } else {
                setTxState(p => ({ ...p, loading: false, data: [] }));
            }
        });
    };

    // ── derived user props ───────────────────────────────────────────────────
    const isVerified = user && !user.verificationToken;
    const isActive   = user && user.status === 1;

    return (
        <>
            <Helmet><title>{pageTitle}</title></Helmet>

            <div className="pxl_mainWrapper">
                <div className="pxl_rightBar_assets">

                    {/* header */}
                    <div className="pxl-rb-header-wrap">
                        <div className="pxl-headingTop">
                            <div className="pxl-ud-heading-row">
                                <Link to="/admin/users" className="pxl-ud-back-btn" aria-label="Back to users">
                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="15 18 9 12 15 6"/>
                                    </svg>
                                    <span>Users</span>
                                </Link>
                                <h2 className="pxl-rb-header-heading">{pageTitle.split(' · ')[0]}</h2>
                            </div>
                        </div>
                        <div className="pxl-rb-header-right">
                            <div className="pxl-header-toogle-btn"
                                onClick={() => store.updateStore('resMenuToggle', !store.resMenuToggle)}>
                                <svg className="pxl-header-toogle-svg" viewBox="0 0 100 100" width="80">
                                    <path className="pxl-header-toogle-svg-line pxl-header-toogle-svg-line-top"
                                        d="m 30,33 h 40 c 3.722839,0 7.5,3.126468 7.5,8.578427 0,5.451959 -2.727029,8.421573 -7.5,8.421573 h -20" />
                                    <path className="pxl-header-toogle-svg-line pxl-header-toogle-svg-line-middle" d="m 30,50 h 40" />
                                    <path className="pxl-header-toogle-svg-line pxl-header-toogle-svg-line-bottom"
                                        d="m 70,67 h -40 c 0,0 -7.5,-0.802118 -7.5,-8.365747 0,-7.563629 7.5,-8.634253 7.5,-8.634253 h 20" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    {/* body */}
                    <div className="pxl-ud-layout">

                        {/* ── Left: profile card ── */}
                        <aside className="pxl-ud-sidebar">
                            <div className="pxl-ud-avatar-wrap">
                                {user?.profilePic
                                    ? <img src={assetsUrlPath(user.profilePic)} alt={user?.name} className="pxl-ud-avatar-img" />
                                    : <div className="pxl-ud-avatar-initials">
                                        {user ? getNameInitials(user.name) : '…'}
                                      </div>
                                }
                            </div>

                            <h3 className="pxl-ud-name">{user?.name || '—'}</h3>
                            <p className="pxl-ud-email">{user?.email || '—'}</p>

                            <div className="pxl-ud-badges">
                                <span className={`pxl-ud-pill ${isVerified ? 'pxl-ud-pill--success' : 'pxl-ud-pill--warning'}`}>
                                    {isVerified ? 'Verified' : 'Not Verified'}
                                </span>
                                <span className={`pxl-ud-pill ${isActive ? 'pxl-ud-pill--active' : 'pxl-ud-pill--muted'}`}>
                                    {isActive ? 'Active' : 'Inactive'}
                                </span>
                            </div>

                            <div className="pxl-ud-info-list">
                                <div className="pxl-ud-info-row">
                                    <span className="pxl-ud-info-label">Role</span>
                                    <span className="pxl-ud-info-value pxl-ud-role-tag">{user?.role || '—'}</span>
                                </div>
                                <div className="pxl-ud-info-row">
                                    <span className="pxl-ud-info-label">Registered</span>
                                    <span className="pxl-ud-info-value">
                                        {user ? moment(user.createdAt).format('DD MMM YYYY') : '—'}
                                    </span>
                                </div>
                                {user?.source && (
                                    <div className="pxl-ud-info-row">
                                        <span className="pxl-ud-info-label">Source</span>
                                        <span className="pxl-ud-info-value">{user.source}</span>
                                    </div>
                                )}
                                {user?.provider && (
                                    <div className="pxl-ud-info-row">
                                        <span className="pxl-ud-info-label">Provider</span>
                                        <span className="pxl-ud-info-value">{user.provider}</span>
                                    </div>
                                )}
                            </div>
                        </aside>

                        {/* ── Right: tabbed content ── */}
                        <div className="pxl-ud-main">

                            {/* tab nav */}
                            <div className="pxl-ud-tabs">
                                {TABS.map((t, i) => (
                                    <button key={i}
                                        className={`pxl-ud-tab-btn${activeTab === i ? ' is-active' : ''}`}
                                        onClick={() => setActiveTab(i)}>
                                        {t}
                                    </button>
                                ))}
                            </div>

                            {/* ── Tab 0: Login History ── */}
                            {activeTab === 0 && (
                                <div className="pxl-ud-panel">
                                    <div className="pxl-ud-panel-header">
                                        <div>
                                            <h5 className="pxl-ud-panel-title">Login History</h5>
                                            <p className="pxl-ud-panel-sub">IP addresses &amp; location per session</p>
                                        </div>
                                        <div className="pxl-ud-date-filter">
                                            <label>Filter by date</label>
                                            <DatePicker
                                                selectsRange
                                                startDate={startDate}
                                                endDate={endDate}
                                                onChange={(update) => setDateRange(update)}
                                                dateFormat="dd/MM/yyyy"
                                                placeholderText="Select range"
                                            />
                                        </div>
                                    </div>

                                    <div className="pxl-tbl-scroll">
                                      <div className="pxl-users-table-wrap">
                                        <table className="pxl_table">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>IP Address</th>
                                                    <th>Country</th>
                                                    <th>City</th>
                                                    <th>Login Date &amp; Time</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loginState.loading ? (
                                                    <tr><td colSpan={5}>{skeletonLoader('table')}</td></tr>
                                                ) : loginState.data.length > 0 ? (
                                                    loginState.data.map((item, i) => {
                                                        const rowNum = (loginState.page - 1) * loginState.limit + i + 1;
                                                        return (
                                                            <tr key={i}>
                                                                <td><p>{rowNum}</p></td>
                                                                <td><p>{item.ip_address}</p></td>
                                                                <td><p>{item?.location?.country || '—'}</p></td>
                                                                <td><p>{item?.location?.city    || '—'}</p></td>
                                                                <td><p>{moment(item.createdAt).format('DD MMM YYYY, hh:mm:ss A')}</p></td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : (
                                                    <NoDataFoundDataTable column={5} statement="No login history found." />
                                                )}
                                            </tbody>
                                        </table>
                                      </div>
                                    </div>

                                    <Pagination
                                        type="Login"
                                        dataRange={{
                                            start: loginState.totalRecords ? (loginState.page - 1) * loginState.limit + 1 : 0,
                                            end:   loginState.totalRecords ? (loginState.page - 1) * loginState.limit + loginState.data.length : 0,
                                        }}
                                        showPagination
                                        currentPage={loginState.page}
                                        totalRecords={loginState.totalRecords}
                                        perPage={loginState.limit}
                                        isLoading={loginState.loading}
                                        onClick={p => setLoginState(prev => ({ ...prev, page: p }))}
                                    />
                                </div>
                            )}

                            {/* ── Tab 1: Subscriptions ── */}
                            {activeTab === 1 && (
                                <div className="pxl-ud-panel">
                                    <div className="pxl-ud-panel-header">
                                        <div>
                                            <h5 className="pxl-ud-panel-title">Subscriptions</h5>
                                            <p className="pxl-ud-panel-sub">Plan subscription payment history</p>
                                        </div>
                                    </div>

                                    <div className="pxl-tbl-scroll">
                                      <div className="pxl-users-table-wrap">
                                        <table className="pxl_table">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Plan</th>
                                                    <th>Amount</th>
                                                    <th>Status</th>
                                                    <th>PayPal Sub ID</th>
                                                    <th>Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {subState.loading ? (
                                                    <tr><td colSpan={6}>{skeletonLoader('table')}</td></tr>
                                                ) : subState.data.length > 0 ? (
                                                    subState.data.map((item, i) => {
                                                        const rowNum = (subState.page - 1) * subState.limit + i + 1;
                                                        const planName = item.plan?.name || item.tier?.name || '—';
                                                        return (
                                                            <tr key={item._id || i}>
                                                                <td><p>{rowNum}</p></td>
                                                                <td><p className="pxl-ud-plan-name">{planName}</p></td>
                                                                <td>
                                                                    <p className="pxl-ud-amount">
                                                                        ${Number(item.amount || 0).toFixed(2)}
                                                                        {item.currency && item.currency !== 'USD' &&
                                                                            <span className="pxl-ud-currency"> {item.currency}</span>}
                                                                    </p>
                                                                </td>
                                                                <td>
                                                                    <StatusPill value={item.status} map={SUBSCRIPTION_STATUS_MAP} />
                                                                </td>
                                                                <td><p style={{ fontSize: 11, color: '#8a8fa6', fontFamily: 'monospace' }}>{item.paypalSubscriptionId || '—'}</p></td>
                                                                <td><p>{moment(item.createdAt).format('DD MMM YYYY, hh:mm A')}</p></td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : (
                                                    <NoDataFoundDataTable column={6} statement="No subscriptions found." />
                                                )}
                                            </tbody>
                                        </table>
                                      </div>
                                    </div>

                                    <Pagination
                                        type="Subscription"
                                        dataRange={{
                                            start: subState.totalRecords ? (subState.page - 1) * subState.limit + 1 : 0,
                                            end:   subState.totalRecords ? (subState.page - 1) * subState.limit + subState.data.length : 0,
                                        }}
                                        showPagination
                                        currentPage={subState.page}
                                        totalRecords={subState.totalRecords}
                                        perPage={subState.limit}
                                        isLoading={subState.loading}
                                        onClick={p => setSubState(prev => ({ ...prev, page: p }))}
                                    />
                                </div>
                            )}

                            {/* ── Tab 2: Transactions ── */}
                            {activeTab === 2 && (
                                <div className="pxl-ud-panel">
                                    <div className="pxl-ud-panel-header">
                                        <div>
                                            <h5 className="pxl-ud-panel-title">Transactions</h5>
                                            <p className="pxl-ud-panel-sub">Payment history for this user</p>
                                        </div>
                                    </div>

                                    <div className="pxl-tbl-scroll">
                                      <div className="pxl-users-table-wrap">
                                        <table className="pxl_table">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Amount</th>
                                                    <th>Plan / Tier</th>
                                                    <th>Type</th>
                                                    <th>Status</th>
                                                    <th>Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {txState.loading ? (
                                                    <tr><td colSpan={6}>{skeletonLoader('table')}</td></tr>
                                                ) : txState.data.length > 0 ? (
                                                    txState.data.map((item, i) => {
                                                        const rowNum = (txState.page - 1) * txState.limit + i + 1;
                                                        const planName = item.tier?.name || item.plan?.name
                                                            || item.tierId?.name || item.productPlanId?.name || '—';
                                                        return (
                                                            <tr key={item._id || i}>
                                                                <td><p>{rowNum}</p></td>
                                                                <td>
                                                                    <p className="pxl-ud-amount">
                                                                        ${Number(item.amount || 0).toFixed(2)}
                                                                        {item.currency && item.currency !== 'USD' &&
                                                                            <span className="pxl-ud-currency"> {item.currency}</span>}
                                                                    </p>
                                                                </td>
                                                                <td><p className="pxl-ud-plan-name">{planName}</p></td>
                                                                <td>
                                                                    <span className="pxl-ud-type-tag">{item.type || '—'}</span>
                                                                </td>
                                                                <td>
                                                                    <StatusPill value={item.status} map={TRANSACTION_STATUS_MAP} />
                                                                </td>
                                                                <td><p>{moment(item.createdAt).format('DD MMM YYYY, hh:mm A')}</p></td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : (
                                                    <NoDataFoundDataTable column={6} statement="No transactions found." />
                                                )}
                                            </tbody>
                                        </table>
                                      </div>
                                    </div>

                                    <Pagination
                                        type="Transaction"
                                        dataRange={{
                                            start: txState.totalRecords ? (txState.page - 1) * txState.limit + 1 : 0,
                                            end:   txState.totalRecords ? (txState.page - 1) * txState.limit + txState.data.length : 0,
                                        }}
                                        showPagination
                                        currentPage={txState.page}
                                        totalRecords={txState.totalRecords}
                                        perPage={txState.limit}
                                        isLoading={txState.loading}
                                        onClick={p => setTxState(prev => ({ ...prev, page: p }))}
                                    />
                                </div>
                            )}

                        </div>
                    </div>

                </div>
            </div>
        </>
    );
};

export default UserDetails;
